/**
 * window.oneskin.cart — the theme's one add-to-cart path. Loaded as a module from layout/theme.liquid,
 * right after oneskin-events.js, on every page.
 *
 *   await window.oneskin.cart.add({ id, quantity, sellingPlan, properties, track, element, openCart })
 *     → { item, items, cart }   throws { code, message } when Shopify refuses the add
 *
 * Every add through it carries what the product page's form always did (it was product.js cartAddItem):
 *   1. selling_plan on the main line when one is passed (Subscribe & save)
 *   2. the free gift: when theme setting "enable_gift_auto_add" is on, the add is a subscription and the variant is
 *      a trigger in window.freeGifts (snippets/free-gifts-data), the gift goes in the same request with
 *      _NSHC_GIFT, unless the cart already has it for this variant (_NSHC_GIFT_FOR)
 *   3. Elevar: oneskin.events.prepare('add_to_cart') before the request, so dl_add_to_cart gets source_location,
 *      component_name, is_subscription, subscription_interval, product fields and item_id
 *   4. a 422 sends cart:error and throws Shopify's message
 *   5. after the add: cart.js is read again, the cart_order storage key is updated, cart:updated and
 *      cart:item-added are sent
 *   6. the hybrid cart opens (openCart: false to skip it)
 *
 * Markup-only buttons (no JS of their own; landing pages, cards, carousels):
 *
 *   <button type="button" data-cart-add data-variant-id="123" data-selling-plan="456"
 *     data-product-title="…" data-product-variant="…" data-product-category="…" data-plan-name="Every 2 months"
 *     data-loading-text="Adding..." data-success-text="Added" data-error-text="Try again">Subscribe & save</button>
 *
 *   data-selling-plan empty or missing = one-time. data-quantity defaults to 1. The button gets the `loading` class,
 *   aria-busy and the loading / success / error text while it works, then goes back to its markup. Each click also
 *   sends quick_add_click, as a product card's quick add does (product-item.js).
 *
 * Don't add cart/add.js fetches anywhere else: call this.
 */

if (!window.oneskin?.cart) {
  const routes = window.theme?.routes?.cart || {}
  const paths = {
    base: `${routes.base || '/cart'}.js`,
    add: `${routes.add || '/cart/add'}.js`,
  }
  const STORAGE_PREFIX = 'fluco_'

  // product.js only sends its global events when the theme turns custom events on; same rule here
  const dispatch = (name, detail) => {
    if (!window.flu?.states?.useCustomEvents) return
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }))
  }

  const getCart = () =>
    fetch(paths.base, { method: 'GET', credentials: 'include' }).then((res) => {
      if (!res.ok) throw new Error(`cart.js returned ${res.status}`)
      return res.json()
    })

  const rememberOrder = (variantId) => {
    if (!variantId) return
    try {
      const order = JSON.parse(localStorage.getItem(`${STORAGE_PREFIX}cart_order`)) || []
      localStorage.setItem(
        `${STORAGE_PREFIX}cart_order`,
        JSON.stringify([variantId, ...order.filter((id) => id !== variantId)])
      )
    } catch {
      // No storage (private mode, blocked): the order is only a nicety
    }
  }

  async function giftFor(variantId) {
    if (!window.theme?.settings?.enable_gift_auto_add || !window.freeGifts) return null
    const gift = window.freeGifts.find((entry) =>
      entry.trigger_variants.some((variant) => variant.variant_id === variantId)
    )
    if (!gift) return null
    const giftLine = { id: gift.gift_variant.variant_id, quantity: 1, properties: { _NSHC_GIFT: true } }
    try {
      const cart = await getCart()
      const inCart = cart.items?.some(
        (item) => item.variant_id === giftLine.id && item.properties?._NSHC_GIFT_FOR === String(variantId)
      )
      return inCart ? null : giftLine
    } catch {
      // Can't read the cart: add the gift, as the product form always has
      return giftLine
    }
  }

  /**
   * @param {object} options
   * @param {number|string} options.id - Variant id
   * @param {number} [options.quantity=1]
   * @param {number|string} [options.sellingPlan] - Selling plan id; leave out for one-time
   * @param {object} [options.properties] - Line item properties
   * @param {object} [options.track] - Extra add_to_cart fields for Elevar (product_name, product_variant,
   *   product_category, subscription_interval, source_location, component_name, placement, is_bundle, bundle_size)
   * @param {Element} [options.element] - The button or form, for oneskin.events.contextFor (source_location,
   *   component_name from the page's analytics markers)
   * @param {boolean} [options.openCart=true]
   */
  async function add({ id, quantity = 1, sellingPlan, properties, track = {}, element, openCart = true } = {}) {
    const variantId = Number(id)
    if (!variantId) throw { code: 0, message: 'Missing variant id' }
    const plan = Number(sellingPlan) || null

    const main = { id: variantId, quantity: Number(quantity) || 1 }
    if (plan) main.selling_plan = plan
    if (properties && Object.keys(properties).length) main.properties = properties
    const items = [main]

    if (plan) {
      const gift = await giftFor(variantId)
      if (gift) items.push(gift)
    }

    const events = window.oneskin?.events
    if (events) {
      const ctx = (element && events.contextFor?.(element)) || {}
      events.prepare('add_to_cart', {
        source_location: ctx.source_location,
        component_name: ctx.component_name,
        is_subscription: !!plan,
        subscription_interval: plan ? undefined : 'one_time',
        item_id: String(variantId),
        ...Object.fromEntries(Object.entries(track).filter(([, value]) => value !== undefined && value !== '')),
      })
    }

    const res = await fetch(paths.add, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items }),
    })
    let body = null
    try {
      body = await res.json()
    } catch {
      // Not JSON: handled below
    }

    if (!res.ok || body?.status == '422') {
      const message = body?.description || body?.message || window.theme?.strings?.cart?.general_error || 'Unable to add item to cart.'
      dispatch('cart:error', { errorMessage: message })
      throw { code: body?.status || res.status, message }
    }

    const added = body?.items || []
    const item = added.find((line) => line.variant_id === variantId) || added[0] || body
    rememberOrder(item?.variant_id)

    let cart = null
    try {
      cart = await getCart()
      dispatch('cart:updated', { cart })
    } catch {
      // The add went through; the hybrid cart refreshes itself when it opens
    }
    dispatch('cart:item-added', { product: item })

    if (openCart) window.NSHybridCart?.open?.()
    return { item, items: added, cart }
  }

  // Markup-only [data-cart-add] buttons
  const restore = (button, html, delay) =>
    setTimeout(() => {
      button.innerHTML = html
      button.disabled = false
      button.classList.remove('loading')
      button.removeAttribute('aria-busy')
    }, delay)

  document.addEventListener('click', async (event) => {
    const button = event.target.closest?.('[data-cart-add]')
    if (!button) return
    event.preventDefault()
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return

    const d = button.dataset
    const variantId = Number(d.variantId)
    if (!variantId) return
    const plan = Number(d.sellingPlan) || null

    const html = button.innerHTML
    button.disabled = true
    button.classList.add('loading')
    button.setAttribute('aria-busy', 'true')
    if (d.loadingText) button.textContent = d.loadingText

    const events = window.oneskin?.events
    if (events) {
      const ctx = events.contextFor?.(button) || {}
      events.track('quick_add_click', {
        product_name: d.productTitle,
        item_id: String(variantId),
        source_location: ctx.source_location,
        component_name: ctx.component_name,
      })
    }

    try {
      await add({
        id: variantId,
        quantity: d.quantity,
        sellingPlan: plan,
        element: button,
        track: {
          product_name: d.productTitle,
          product_variant: d.productVariant,
          product_category: d.productCategory,
          subscription_interval: plan && d.planName ? d.planName.replace(/\s+/g, '_').toLowerCase() : undefined,
        },
      })
      if (d.successText) button.textContent = d.successText
      restore(button, html, d.successText ? 1400 : 0)
    } catch (error) {
      console.error('[cart-add]', error)
      if (d.errorText) button.textContent = d.errorText
      restore(button, html, d.errorText ? 2000 : 0)
    }
  })

  window.oneskin ||= {}
  window.oneskin.cart = { add, getCart }
}
